import type { Spread } from '@/api/types/spread'
import type { PageSide } from '@/lib/photos/types/page-side'
import { spreadPagesForMenubarIndicator } from '@/lib/spreads/spread-page-numbers'

interface ResolvePlacedPagesInput {
  /** Printed page numbers the server lists for the photo; `undefined` until its details load. */
  serverPages: readonly number[] | undefined
  /** What this session knows of the photo per spread: the pages (sides) of each spread it is on.
   *  An empty set means the session knows it is not on that spread (`PhotoPlacementSides`). */
  sidesBySpread: ReadonlyMap<string, ReadonlySet<PageSide>> | undefined
  /** The book's spreads, whose `pages[].number` are the printed page numbers. */
  spreads: readonly Spread[] | undefined
}

/**
 * The printed page numbers a photo is on right now, ascending: the server's list with this
 * session's own knowledge laid over it. The server's list is only as fresh as its last indexing, so
 * a placement made here must show at once and a removal must stick, the way the trays'
 * `applyPhotoPlacementOverrides` / `applyPortraitPlacementOverrides` overlay their flags.
 *
 * For every spread the session knows, its page numbers are exactly the sides it recorded: the
 * server's numbers for both pages of that spread are dropped and the recorded sides' numbers are
 * added, so moving a photo from the left page to the right one swaps 12 for 13. Spreads the session
 * has not seen keep whatever the server lists for them.
 *
 * Returns `null` when no page list can be worked out, so the caller keeps its flag-only fallback:
 * the server list has not loaded and the session has nothing to add, or the session has the photo
 * on a page whose number is unknown (the spread is not in the book's list yet). Returning a list
 * then would drop that placement and read "Not placed" for a photo that is on the canvas.
 */
export function resolvePlacedPages({
  serverPages,
  sidesBySpread,
  spreads,
}: ResolvePlacedPagesInput): number[] | null {
  const spreadsById = new Map((spreads ?? []).map((spread) => [spread.spreadId, spread]))
  const dropped = new Set<number>()
  const added = new Set<number>()

  for (const [spreadId, sides] of sidesBySpread ?? []) {
    const pages = spreadPagesForMenubarIndicator(spreadsById.get(spreadId) ?? null)

    for (const pageNumber of [pages?.left, pages?.right]) {
      if (pageNumber != null) dropped.add(pageNumber)
    }

    for (const side of sides) {
      const pageNumber = side === 'left' ? pages?.left : pages?.right

      if (pageNumber == null) return null

      added.add(pageNumber)
    }
  }

  if (serverPages === undefined && added.size === 0) return null

  const pages = new Set<number>()

  for (const pageNumber of serverPages ?? []) {
    if (!dropped.has(pageNumber)) pages.add(pageNumber)
  }

  for (const pageNumber of added) {
    pages.add(pageNumber)
  }

  return [...pages].sort((a, b) => a - b)
}

/** A string that changes exactly when a photo's recorded pages change (spread ids and sides, in any
 *  order), so a caller can react to this photo's own placements and ignore every other photo's. */
export function placementSidesSignature(
  sidesBySpread: ReadonlyMap<string, ReadonlySet<PageSide>> | undefined
): string {
  if (!sidesBySpread) return ''

  return [...sidesBySpread]
    .map(([spreadId, sides]) => `${spreadId}:${[...sides].sort().join('+')}`)
    .sort()
    .join('|')
}

/** "13, 46, 101", or "Not placed" for none. */
export function formatPlacedPages(pages: readonly number[]): string {
  return pages.length > 0 ? pages.join(', ') : 'Not placed'
}
